import { supabase } from '../lib/supabase';
import type { NotificationType } from './notificationService';

// Preferências de notificação por categoria. Ficam em notification_preferences,
// uma linha por tipo (unique user_id + organization_id + type); só a coluna
// in_app é usada — e-mail e push ainda não existem.
//
// Quem cria a notificação de outra pessoa (atribuição, menção) não enxerga as
// preferências dela (RLS: user_id = auth.uid()). Por isso a preferência vale
// em dois pontos do lado de quem recebe: o sino e a página escondem o que está
// desligado, e os verificadores automáticos (prazo, cronômetro, horas) nem
// geram o aviso.

export interface NotificationCategory {
  key: string;
  label: string;
  description: string;
  types: NotificationType[];
  adminOnly?: boolean;
}

export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    key: 'activities',
    label: 'Atividades atribuídas a você',
    description: 'Quando alguém te coloca como responsável por uma atividade ou nota.',
    types: ['task_assigned'],
  },
  {
    key: 'deadlines',
    label: 'Prazos das suas atividades',
    description: 'Atividades vencendo amanhã, hoje ou atrasadas.',
    types: ['task_due_soon', 'task_overdue'],
  },
  {
    key: 'timer',
    label: 'Cronômetro esquecido',
    description: 'Seu cronômetro ligado há mais de 8 horas.',
    types: ['timer_running_long'],
  },
  {
    key: 'project_hours',
    label: 'Horas previstas dos projetos',
    description: 'Projetos que chegaram a 80% ou passaram das horas previstas.',
    types: ['project_hours_budget'],
    adminOnly: true,
  },
];

export function categoryForType(type: string | undefined): NotificationCategory | null {
  if (!type) return null;
  return NOTIFICATION_CATEGORIES.find(c => (c.types as string[]).includes(type)) ?? null;
}

export const PREFERENCES_CHANGED_EVENT = 'notification-preferences-changed';

const cache = new Map<string, Promise<Set<string>>>();

function cacheKey(organizationId: string, userId: string) {
  return `${organizationId}:${userId}`;
}

// Tipos desligados pela pessoa. Falha (inclusive tabela ausente) devolve vazio:
// na dúvida, avisa — perder um aviso é pior que receber um a mais.
export function getDisabledTypes(organizationId: string, userId: string): Promise<Set<string>> {
  const key = cacheKey(organizationId, userId);
  const emCache = cache.get(key);
  if (emCache) return emCache;

  const promessa = (async () => {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('type, in_app')
      .eq('user_id', userId)
      .eq('organization_id', organizationId);
    if (error) {
      console.warn('Preferências de notificação indisponíveis; mostrando tudo:', error);
      cache.delete(key);
      return new Set<string>();
    }
    const desligados = new Set<string>();
    for (const linha of (data ?? []) as Array<{ type: string; in_app: boolean | null }>) {
      if (linha.in_app === false) desligados.add(linha.type);
    }
    return desligados;
  })();

  cache.set(key, promessa);
  return promessa;
}

export async function isNotificationTypeEnabled(
  organizationId: string,
  userId: string,
  type: NotificationType
): Promise<boolean> {
  const desligados = await getDisabledTypes(organizationId, userId);
  return !desligados.has(type);
}

export async function setCategoryEnabled(
  organizationId: string,
  userId: string,
  category: NotificationCategory,
  enabled: boolean
): Promise<void> {
  const linhas = category.types.map(type => ({
    user_id: userId,
    organization_id: organizationId,
    type,
    in_app: enabled,
  }));
  const { error } = await supabase
    .from('notification_preferences')
    .upsert(linhas as never, { onConflict: 'user_id,organization_id,type' });
  if (error) throw error;

  cache.delete(cacheKey(organizationId, userId));
  window.dispatchEvent(new Event(PREFERENCES_CHANGED_EVENT));
}
